import type { ApprovalDetail } from '@fos/shared/contracts/approvals';
import { BILLING_DUE_DAYS_OPTIONS } from '@fos/shared/contracts/billing';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { useApprovalsRefresh } from '../hooks';

type Payload = Record<string, unknown>;

interface OfferRecipient {
  contactId: string;
  email: string;
  name: string | null;
}

/**
 * Jaki formularz edycji pasuje do akcji (pola dozwolone przez typ, `editableFields`). Mail: temat i treść — `body`
 * (przypomnienie o płatności, oferta) albo `bodyText` (wysyłka z Poczty, `email.send`; audyt końcowy Fali 6).
 */
export function editFormKind(detail: Pick<ApprovalDetail, 'editableFields' | 'payload'>): 'email' | 'invoice' | null {
  const fields = new Set(detail.editableFields);
  if (!detail.payload || typeof detail.payload !== 'object') return null;
  if (fields.has('subject') && (fields.has('body') || fields.has('bodyText'))) return 'email';
  if (fields.has('dueDays')) return 'invoice';
  return null;
}

/**
 * „Edytuj” (Figma 03, MVP §10): mail — odbiorcy, temat, treść; faktura — termin płatności. Zapis tworzy nową rewizję
 * z nowym skrótem treści; wcześniejsza zgoda nie obowiązuje, akcję trzeba zatwierdzić ponownie. Odbiorcy oferty
 * tylko spośród kontaktów klienta (serwer i tak to sprawdza).
 */
export function EditApprovalDialog({
  detail,
  open,
  onOpenChange,
}: {
  detail: ApprovalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const kind = editFormKind(detail);
  const payload = (detail.payload ?? {}) as Payload;
  const fields = new Set(detail.editableFields);
  // pole treści typu (`email.send`: `bodyText`) i czy odbiorców wolno zmienić (`email.send` — nie: zmiana adresata
  // = nowy szkic w Poczcie, z kontrolą kontaktów klienta)
  const bodyKey = fields.has('bodyText') ? 'bodyText' : 'body';
  const recipientsEditable = fields.has('to');
  const fixedRecipients = Array.isArray(payload.to)
    ? (payload.to as { email?: unknown; name?: unknown }[])
        .map((a) =>
          typeof a?.email === 'string'
            ? typeof a.name === 'string' && a.name
              ? `${a.name} <${a.email}>`
              : a.email
            : null,
        )
        .filter((a): a is string => a !== null)
        .join(', ')
    : typeof payload.to === 'string'
      ? payload.to
      : '';
  const refresh = useApprovalsRefresh();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [to, setTo] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dueDays, setDueDays] = useState('14');

  const offerId = typeof payload.offerId === 'string' && Array.isArray(payload.to) ? payload.offerId : null;
  const options = trpc.offers.sendOptions.useQuery({ id: offerId ?? '' }, { enabled: open && offerId !== null });

  // formularz z bieżącej rewizji przy otwarciu (i gdy treść zmieni się na serwerze)
  const base = detail.payload;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu albo nowej rewizji (skrót treści)
  useEffect(() => {
    if (!open) return;
    const p = (base ?? {}) as Payload;
    setErrors({});
    if (Array.isArray(p.to)) setRecipients((p.to as OfferRecipient[]).map((r) => r.contactId));
    else setTo(typeof p.to === 'string' ? p.to : '');
    setSubject(typeof p.subject === 'string' ? p.subject : '');
    const text = p[fields.has('bodyText') ? 'bodyText' : 'body'];
    setBody(typeof text === 'string' ? text : '');
    setDueDays(String(typeof p.dueDays === 'number' ? p.dueDays : 14));
  }, [open, detail.payloadHash]);

  const contacts = useMemo(() => {
    const current = Array.isArray(payload.to) ? (payload.to as OfferRecipient[]) : [];
    const list: OfferRecipient[] = (options.data?.contacts ?? []).map((c) => ({
      contactId: c.id,
      email: c.email,
      name: c.name,
    }));
    for (const r of current) if (!list.some((c) => c.contactId === r.contactId)) list.push(r);
    return list;
  }, [options.data, payload.to]);

  const edit = trpc.approvals.edit.useMutation({
    onSuccess: async () => {
      toast.success('Zapisano nową wersję treści. Zatwierdź ją ponownie.');
      onOpenChange(false);
      await refresh();
    },
    onError: async (err) => {
      const issues = validationIssues(err);
      if (issues.length > 0) {
        const next: Record<string, string> = {};
        for (const i of issues) {
          const key = i.path.replace(/^payload\.?/, '').split('.')[0] || 'form';
          next[key] ??= i.message;
        }
        setErrors(next);
        return;
      }
      toast.error(errorMessage(err, 'Nie udało się zapisać zmian.'));
      if (appErrorCode(err) === 'CONFLICT') {
        onOpenChange(false);
        await refresh();
      }
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    let next: Payload = { ...payload };
    if (kind === 'email') {
      // odbiorcy tylko gdy typ pozwala ich zmienić (inaczej bez zmian w treści)
      if (recipientsEditable && Array.isArray(payload.to)) {
        const chosen = contacts.filter((c) => recipients.includes(c.contactId));
        if (chosen.length === 0) {
          setErrors({ to: 'Wybierz odbiorcę' });
          return;
        }
        next.to = chosen;
      } else if (recipientsEditable) {
        const email = to.trim();
        next.to = email;
        if (email !== payload.to && 'toName' in payload) next.toName = null;
      }
      next = { ...next, subject: subject.trim(), [bodyKey]: body };
    } else if (kind === 'invoice') {
      next.dueDays = Number(dueDays);
    }
    edit.mutate({ id: detail.id, basePayloadHash: detail.payloadHash, payload: next });
  }

  if (!kind) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={kind === 'email' ? 'Edytuj wiadomość' : 'Edytuj fakturę'}
      description="Po zapisaniu powstanie nowa wersja treści. Wcześniejsza zgoda przestanie obowiązywać."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form={`edit-${detail.id}`} loading={edit.isPending}>
            Zapisz zmiany
          </Button>
        </>
      }
    >
      <form id={`edit-${detail.id}`} onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {kind === 'email' && (
          <>
            {!recipientsEditable ? (
              <div className="flex flex-col gap-1">
                <span className="text-caption font-medium text-fg-secondary">Do</span>
                <p className="text-body-s text-fg">{fixedRecipients}</p>
                <p className="text-caption text-fg-muted">Odbiorcę zmienisz w szkicu w Poczcie.</p>
              </div>
            ) : Array.isArray(payload.to) ? (
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1.5 text-caption font-medium text-fg-secondary">Odbiorcy</legend>
                {contacts.map((c) => (
                  <Checkbox
                    key={c.contactId}
                    checked={recipients.includes(c.contactId)}
                    onCheckedChange={(on) =>
                      setRecipients((prev) => (on ? [...prev, c.contactId] : prev.filter((id) => id !== c.contactId)))
                    }
                    label={c.name ? `${c.name} <${c.email}>` : c.email}
                  />
                ))}
                {errors.to && (
                  <p role="alert" className="text-caption text-danger">
                    {errors.to}
                  </p>
                )}
              </fieldset>
            ) : (
              <Field label="Do" error={errors.to}>
                <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
              </Field>
            )}
            <Field label="Temat" error={errors.subject}>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label="Treść" error={errors[bodyKey]}>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
            </Field>
          </>
        )}
        {kind === 'invoice' && (
          <Field label="Termin płatności" error={errors.dueDays}>
            <Select
              value={dueDays}
              onValueChange={setDueDays}
              options={BILLING_DUE_DAYS_OPTIONS.map((d) => ({ value: String(d), label: `${d} dni` }))}
            />
          </Field>
        )}
        {errors.form && (
          <p role="alert" className="text-caption text-danger">
            {errors.form}
          </p>
        )}
      </form>
    </Dialog>
  );
}

/** „Odrzuć” z opcjonalnym powodem (trafia do historii akcji). Nic się nie wykona. */
export function RejectApprovalDialog({
  approval,
  open,
  onOpenChange,
  onRejected,
}: {
  approval: { id: string; summary: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRejected?: () => void;
}) {
  const [note, setNote] = useState('');
  const refresh = useApprovalsRefresh();
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  const reject = trpc.approvals.reject.useMutation({
    onSuccess: async () => {
      toast.success('Odrzucono. Nic się nie wykona.');
      onOpenChange(false);
      onRejected?.();
      await refresh();
    },
    onError: async (err) => {
      toast.error(errorMessage(err, 'Nie udało się odrzucić akcji.'));
      if (appErrorCode(err) === 'CONFLICT') {
        onOpenChange(false);
        await refresh();
      }
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title="Odrzucić akcję?"
      description={approval.summary}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={reject.isPending}
            onClick={() => reject.mutate({ id: approval.id, ...(note.trim() ? { note: note.trim() } : {}) })}
          >
            Odrzuć
          </Button>
        </>
      }
    >
      <Field label="Powód (opcjonalnie)" hint="Zobaczy go autor akcji w historii.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} />
      </Field>
    </Dialog>
  );
}
